import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * ReplaceAllModal Component
 * 
 * Modal for replacing all occurrences of selected text
 */
const ReplaceAllModal = ({
  isOpen,
  onClose,
  onReplace,
  searchText,
  occurrenceCount = 0,
}) => {
  const [replaceText, setReplaceText] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
      setReplaceText('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!replaceText.trim()) {
      return;
    }

    setIsReplacing(true);
    try {
      await onReplace(searchText, replaceText.trim());
      onClose();
    } catch (error) {
      console.error('Replace failed:', error);
    } finally {
      setIsReplacing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 100000 }} // Above toolbar (99999) and all other elements
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Replace All Occurrences
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Found {occurrenceCount} occurrence{occurrenceCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Search text (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Find:
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-mono text-sm">
              "{searchText}"
            </div>
          </div>

          {/* Replace text input */}
          <div>
            <label
              htmlFor="replace-input"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Replace with:
            </label>
            <input
              ref={inputRef}
              id="replace-input"
              type="text"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter replacement text..."
              className="
                w-full px-3 py-2 
                border border-gray-300 rounded-lg
                focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                text-gray-900
              "
              disabled={isReplacing}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 
                border border-gray-300 rounded-lg
                text-gray-700 hover:bg-gray-50
                transition-colors
              "
              disabled={isReplacing}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!replaceText.trim() || isReplacing}
              className="
                px-4 py-2 
                bg-blue-600 hover:bg-blue-700 
                text-white rounded-lg
                transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              {isReplacing ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Replacing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-4 w-4" />
                  Replace All ({occurrenceCount})
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReplaceAllModal;

